import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useAnalyticsOverview,
  useTopPieces,
  useBestTimes,
  useTriggerSync,
} from "@/hooks/use-analytics";
import { SyncStatusHeader } from "@/components/analytics/sync-status-header";
import {
  TimeRangeSelector,
  type AnalyticsRange,
} from "@/components/analytics/time-range-selector";
import { KpiStrip } from "@/components/analytics/kpi-strip";
import { DailyTrendChart } from "@/components/analytics/daily-trend-chart";
import { PlatformMix } from "@/components/analytics/platform-mix";
import { BestTimeHeatmap } from "@/components/analytics/best-time-heatmap";
import {
  TopPiecesTable,
  type TopPiecesSort,
} from "@/components/analytics/top-pieces-table";
import { PieceTimelineDrawer } from "@/components/analytics/piece-timeline-drawer";
import { OperationalStrip } from "@/components/analytics/operational-strip";
import { AddonUpsellCard } from "@/components/analytics/addon-upsell-card";
import { InsightsPanel } from "@/components/analytics/insights-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { ArrowRight } from "lucide-react";

function AnalyticsLoadingSkeleton() {
  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <Skeleton className="h-12 w-full" />
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-48" />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

interface EmptyStateCardProps {
  brandId: string;
  canTriggerSync: boolean;
}

function EmptyStateCard({ brandId, canTriggerSync }: EmptyStateCardProps) {
  const { t } = useTranslation("analytics");
  const triggerSync = useTriggerSync(brandId);
  const handleRefresh = () => {
    triggerSync.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("page.syncStarted"));
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "";
        if (msg.includes("429") || /rate/i.test(msg)) {
          toast.error(t("sync.rateLimited"));
        } else {
          toast.error(t("sync.syncFailed"));
        }
      },
    });
  };

  return (
    <div className="rounded-lg border bg-card p-6">
      <p className="text-sm text-muted-foreground">
        {canTriggerSync ? t("page.noDataCta") : t("page.noData")}
      </p>
      {canTriggerSync && (
        <Button
          className="mt-3"
          size="sm"
          onClick={handleRefresh}
          disabled={triggerSync.isPending}
        >
          {triggerSync.isPending ? t("page.starting") : t("page.refreshNow")}
        </Button>
      )}
    </div>
  );
}

interface PlatformDeepDive {
  platform: string;
  to: string;
  label: string;
  description: string;
}

const DEEP_DIVE_REGISTRY: Record<string, Omit<PlatformDeepDive, "platform" | "to" | "description">> = {
  INSTAGRAM: {
    label: "Instagram",
  },
  YOUTUBE: {
    label: "YouTube",
  },
};

function PlatformDeepDives({
  brandSlug,
  availablePlatforms,
}: {
  brandSlug: string;
  availablePlatforms: string[];
}) {
  const { t } = useTranslation("analytics");
  const DEEP_DIVE_PATHS: Record<string, string> = {
    INSTAGRAM: "instagram",
    YOUTUBE: "youtube",
  };
  const DEEP_DIVE_DESCRIPTIONS: Record<string, string> = {
    INSTAGRAM: t("page.instagramDeepDiveDesc"),
    YOUTUBE: t("youtube.deepDiveCard"),
  };

  const items: PlatformDeepDive[] = availablePlatforms
    .filter((p) => p in DEEP_DIVE_REGISTRY)
    .map((p) => ({
      platform: p,
      to: DEEP_DIVE_PATHS[p]
        ? `/${brandSlug}/analytics/${DEEP_DIVE_PATHS[p]}`
        : `/${brandSlug}/analytics`,
      description: DEEP_DIVE_DESCRIPTIONS[p] ?? "",
      ...DEEP_DIVE_REGISTRY[p],
    }));

  if (items.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        {t("page.deepDives")}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((d) => (
          <Link
            key={d.platform}
            to={d.to}
            className="group flex items-start gap-3 rounded-lg border bg-card p-4 hover:bg-accent transition-colors"
          >
            <PlatformIcon platform={d.platform} size={24} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{d.label}</span>
                <ArrowRight
                  size={16}
                  className="text-muted-foreground transition-transform group-hover:translate-x-0.5"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {d.description}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BrandAnalyticsPage() {
  const { t } = useTranslation("analytics");
  const { brand } = useBrandContext();
  const { canViewAnalytics, canTriggerSync } = usePermissions();
  const [days, setDays] = useState<AnalyticsRange>(30);
  const [sortBy, setSortBy] = useState<TopPiecesSort>("impressions");
  const [selectedPieceId, setSelectedPieceId] = useState<string | null>(null);
  const [btPlatform, setBtPlatform] = useState<string | undefined>(undefined);

  const overview = useAnalyticsOverview(brand?.id ?? "", days);
  const top = useTopPieces(brand?.id ?? "", days, sortBy, 10);
  const bestTimes = useBestTimes(brand?.id ?? "", btPlatform);

  if (!canViewAnalytics) {
    return (
      <p className="p-6 text-muted-foreground">
        {t("page.noAccess")}
      </p>
    );
  }
  if (overview.isLoading) return <AnalyticsLoadingSkeleton />;
  if (overview.isError) {
    return <p className="p-6 text-destructive">{t("page.loadError")}</p>;
  }

  const data = overview.data!;
  const allAddonMissing = !data.addon.available;
  const isEmpty =
    data.addon.available && (data.daily?.length ?? 0) === 0;
  const availablePlatforms: string[] = (data.byPlatform ?? []).map(
    (p: { platform: string }) => p.platform,
  );

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <SyncStatusHeader brandId={brand.id} />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
        <TimeRangeSelector value={days} onChange={setDays} />
      </div>

      <InsightsPanel brandId={brand.id} />

      {allAddonMissing ? (
        <>
          <AddonUpsellCard />
          <OperationalStrip
            stats={data.operational}
            brandSlug={brand.slug}
          />
        </>
      ) : isEmpty ? (
        <>
          <EmptyStateCard
            brandId={brand.id}
            canTriggerSync={canTriggerSync}
          />
          <OperationalStrip
            stats={data.operational}
            brandSlug={brand.slug}
          />
        </>
      ) : (
        <>
          <KpiStrip kpi={data.kpi} video={data.video} />
          <DailyTrendChart daily={data.daily} byPlatform={data.byPlatform} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <PlatformMix byPlatform={data.byPlatform} addon={data.addon} />
            <BestTimeHeatmap
              slots={bestTimes.data?.slots ?? []}
              topSlot={bestTimes.data?.topSlot}
              platform={btPlatform}
              onPlatformChange={setBtPlatform}
              availablePlatforms={availablePlatforms}
            />
          </div>
          <TopPiecesTable
            pieces={top.data?.pieces ?? []}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onSelectPiece={setSelectedPieceId}
          />
          <PlatformDeepDives
            brandSlug={brand.slug}
            availablePlatforms={availablePlatforms}
          />
          <OperationalStrip
            stats={data.operational}
            brandSlug={brand.slug}
          />
        </>
      )}

      <PieceTimelineDrawer
        brandId={brand.id}
        pieceId={selectedPieceId}
        onClose={() => setSelectedPieceId(null)}
      />
    </div>
  );
}
